import { defineMessages, useIntl } from 'react-intl';
import { generatePath, useParams } from 'react-router';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { iconEarthAmericas } from '@sorare/core/src/atoms/icons/system/iconEarthAmericas.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelM } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_MARKET_SCOUT,
  FOOTBALL_MARKET_SCOUT_SO5_COMPETITION_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import {
  buildMarketTabRoute,
  useNavigableMarketTabParams,
} from '@sorare/marketplace/src/hooks/useNavigableMarketTabParams';
import {
  TEAM_MODE_URL_KEY,
  useDerivedTeamMode,
} from '@sorare/marketplace/src/searchCommonPlayer/teamMode';

const Root = styled(Vertical)`
  width: 100%;
  border-bottom: 1px solid var(--c-nd-150);
  @media ${tabletAndAbove} {
    padding: 0 var(--triple-unit);
  }
`;

const So5CompetitionLogo = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;
`;

const StyledTabBar = styled(TabBar)`
  border-bottom: 1px solid transparent;
  --bar-bg-color-custom: transparent;
  &.flat {
    --bar-border-bottom: 0px solid var(--c-nd-100);
  }
  @media ${tabletAndAbove} {
    --bar-gap-custom: var(--unit);
  }
`;

const NationalTeamsIcon = styled(FontAwesomeIcon)`
  font-size: var(--quadruple-unit);
`;

const messages = defineMessages({
  nationalTeams: { defaultMessage: 'National teams' },
});

export const ScoutingCenterBySo5Competition = () => {
  const { formatMessage } = useIntl();
  const { filterQuery } = useNavigableMarketTabParams();
  const { defaultSo5Competition, scoutableSo5Competitions } =
    useSo5Competitions();
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const teamMode = useDerivedTeamMode(!!routeSlug);
  const allTabRoute = generateSportPath(ANY_SPORT_MARKET_SCOUT, {
    sport: Sport.FOOTBALL,
  });
  const nationalTeamsQuery = [filterQuery, `${TEAM_MODE_URL_KEY}=NATIONAL_TEAM`]
    .filter(Boolean)
    .join('&');

  return (
    <Root>
      <StyledTabBar
        grow
        variant="flat"
        fullWidth
        ignoreQueryParams
        value={teamMode}
      >
        <Tab
          key={defaultSo5Competition.slug}
          value="ALL"
          to={buildMarketTabRoute(allTabRoute, filterQuery)}
        >
          <Vertical gap={1} center className="text-center">
            <StarBall color="currentColor" />
            <LabelM bold>{defaultSo5Competition.displayName}</LabelM>
          </Vertical>
        </Tab>
        <Tab
          key="national-teams"
          value="NATIONAL_TEAM"
          to={buildMarketTabRoute(allTabRoute, nationalTeamsQuery)}
        >
          <Vertical gap={1} center className="text-center">
            <NationalTeamsIcon icon={iconEarthAmericas} />
            <LabelM bold>{formatMessage(messages.nationalTeams)}</LabelM>
          </Vertical>
        </Tab>
        {scoutableSo5Competitions.map(so5Competition => (
          <Tab
            key={so5Competition.slug}
            to={buildMarketTabRoute(
              generatePath(FOOTBALL_MARKET_SCOUT_SO5_COMPETITION_SLUG, {
                slug: so5Competition.slug,
              }),
              filterQuery
            )}
          >
            <Vertical gap={1} center className="text-center">
              <So5CompetitionLogo src={so5Competition.iconUrl} />
              <LabelM bold>{so5Competition.displayName}</LabelM>
            </Vertical>
          </Tab>
        ))}
      </StyledTabBar>
    </Root>
  );
};
